import { NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { PopupPosition, BottomDivPosition, ToolTipPosition } from "./tooltip.util";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  standalone: true,
  imports: [NgIf, NgClass],
})
export class TooltipComponent implements OnInit {
  /**
   * Default vertical offset to position the tooltip slightly above the component that triggered it.
   */
  private readonly upShift = '0.5em';

  /**
   * If you want the tooltip to be statically positioned inside a container, you can leave this input undefined.
   * Note: in this case, the tooltip will occupy space and may shift other HTML elements,
   * as it will use `position: relative`.
   * Otherwise, provide the coordinates (DOMRect) of the component that triggers the tooltip
   * to position it dynamically.
   */
  @Input() coordinates: DOMRect;
  @Input() title: string;
  @Input() text: string;
  @Input() xShift = '0em';
  @Input() yShift = '0em';
  @Input() dataQa: string;
  @Input() additionalClassTitle: string;
  @Input() additionalClassText: string;
  /**
   * This flag indicates if the tooltip are using inside element which has position: relative.
   * If it is true, tooltip will calculate its {@link popUpPosition} in another way (because it will be fixed),
   * calculate {@link bottomDivPosition} in another way.
   */
  @Input() isParentPositionRelative: boolean = false;
  /**
   * If you need to keep the tooltip visible when moving the cursor over it,
   * set this flag to true.
   *
   * When enabled, the component will create a `div` under the tooltip
   * with the same coordinates, width, and height as the component that triggered the tooltip.
   * This `div` will become part of the tooltip.
   *
   * To ensure proper behavior, add a `mouseenter` event to the parent component
   * to trigger tooltip display and a `mouseleave` event to the tooltip itself
   * to hide it when the cursor moves away.
   */
  @Input() freezeOnHover: boolean = false;
  /**
   * This flag indicates if the tooltip will have scroll or not.
   * If it is true, then tooltip will have max-height: 10rem and overflow-y: auto.
   */
  @Input() hasScroll: boolean = false;
  /**
   * This flag indicates if the tooltip has the (click) event for copying its text
   * into clipboard. It will copy {@link tooltip.nativeElement.textContent}
   */
  @Input() isCopyTextOnHoverClick: boolean = false;
  /**
   * Defines the tooltip's position relative to the target element.
   *
   * @input positionType
   * @type {@link ToolTipPosition}
   * @default 'centered'
   *
   * Example usage:
   * ```html
   * <app-tooltip [positionType]="'left'"/>
   * ```
   */
  @Input() positionType: ToolTipPosition = 'centered';

  @ViewChild('tooltip')
  private tooltip!: ElementRef<HTMLElement>;

  popUpPosition: PopupPosition;
  bottomDivPosition: BottomDivPosition;

  constructor(
    private notificationService: NotificationsService,
  ) { }

  ngOnInit(): void {
    this.calculatePosition();
  }

  async copyText(event: Event): Promise<void> {
    // Need to stop propagation here, because we do not want to call click of parents
    // when we are just copying text from tooltip
    // For example: HorNavBar -> search company -> onHover shows organization name ->
    // if we click on tooltip we should only copy the name and not click for redirect to company
    event.stopPropagation();
    await navigator.clipboard.writeText(this.tooltip.nativeElement.textContent);
    this.notificationService.success(NOTIFICATIONS.COPIED_NO_TITLE);
  }

  /**
   * While scrolling the text inside the tooltip, if the scroll reaches the start or end,
   * we should prevent the main page from scrolling as long as the mouse is still over the tooltip.
   */
  scrollControl(event: WheelEvent): void {
    const el = this.tooltip.nativeElement;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const stepInPX = event.deltaY;

    const isCurrentScrollAtTop = scrollTop === 0;
    const isCurrentScrollAtBottom = scrollTop + clientHeight >= scrollHeight;

    const isScrollDirectionTop = stepInPX < 0;
    const isScrollDirectionDown = stepInPX > 0;

    const isScrollBlocked = (isScrollDirectionDown && isCurrentScrollAtBottom) || (isScrollDirectionTop && isCurrentScrollAtTop);

    if (isScrollBlocked) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private calculatePosition(): void {
    if (this.coordinates) {
      const posX = this.positionTypeToXCoordinate();
      this.definePopUpPosition(posX);
      this.defineBottomDivPosition();
    }
  }

  private positionTypeToXCoordinate(): number {
    switch (this.positionType) {
      case 'centered':
        return this.coordinates.left + this.coordinates.width / 2;
      case 'left':
        return this.coordinates.left;
      case 'right':
        return this.coordinates.right;
    }
  }

  private definePopUpPosition(posX: number): void {
    if (this.isParentPositionRelative) {
      switch (this.positionType) {
        case 'centered':
          this.popUpPosition = {
            left: `calc(${posX}px + ${this.xShift})`,
            transform: 'translateX(-50%)',
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift})`,
          };
          break;
        case 'left':
          this.popUpPosition = {
            left: `${posX}px`,
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift})`,
          };
          break;
        case 'right':
          this.popUpPosition = {
            right: `calc(${window.innerWidth - posX}px + ${this.xShift})`,
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift})`,
          };
          break;
      }
    } else {
      switch (this.positionType) {
        case 'centered':
          this.popUpPosition = {
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift} - ${window.scrollY}px)`,
            left: `calc(${posX}px + ${this.xShift})`,
            transform: 'translateX(-50%)',
          };
          break;
        case 'left':
          this.popUpPosition = {
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift} - ${window.scrollY}px)`,
            left: `calc(${posX}px + ${this.xShift})`,
          };
          break;
        case 'right':
          this.popUpPosition = {
            bottom: `calc(100% - ${this.coordinates.top}px + ${this.upShift} + ${this.yShift} - ${window.scrollY}px)`,
            left: `calc(${posX}px + ${this.xShift})`,
            transform: 'translateX(-100%)',
          };
          break;
      }
    }
  }

  private defineBottomDivPosition(): void {
    if (this.isParentPositionRelative) {
      this.bottomDivPosition = {
        width: `${this.coordinates.width}px`,
        height: `calc(${this.coordinates.height}px + ${this.upShift} + ${this.yShift})`,
        bottom: '0',
      };
    } else {
      this.bottomDivPosition = {
        width: `calc(0.25em + ${this.coordinates.width}px)`,
        height: `calc(0.25em + ${this.coordinates.height}px)`,
        bottom: `calc(-0.25em - ${this.coordinates.height}px)`,
      };
    }
  }
}

